<template>
    <div class="h-100">
        <div v-if="!isBrowserIE" class="back-off h-100">
            <topbar
                ref="topbar"
                :user="user"
                :entries="sidebarEntries"
                :account-panel-open="accountPanelOpen"
                @toggle-account-panel="toggleAccountPanel" />
            <div class="container-fluid">
                <div class="row">
                    <main role="main" class="default me-sm-auto mt-5 col-12" @click="closeAccountPanel">
                        <notifications style="z-index: 9999" group="notification" />
                        <slot />
                    </main>
                    <transition name="account-panel-slide">
                        <sidebar
                            v-if="showSidebar"
                            class="account-panel-drawer"
                            :style="accountPanelStyle"
                            :user="user"
                            :entries="sidebarEntries"
                            @logout="logout"
                            @close="closeAccountPanel"
                            @reload-router-view="$emit('reload-router-view')" />
                    </transition>
                </div>
            </div>
        </div>
        <div v-else style="text-align: center">
            <h1>{{ $tl("errors.browser.invalid.title") }}</h1>
            <p v-html="$t('errors.browser.invalid.infos')"></p>
        </div>
    </div>
</template>

<script>
import Topbar from "./Topbar.vue";
import Sidebar from "./Sidebar.vue";
import RolesEnum from "../../mixins/enums/RolesEnum";

export default {
    name: "DefaultLayout",
    props: {
        user: {
            required: true,
        },
    },
    mixins: [RolesEnum],
    computed: {
        isAdmin() {
            return this.$store.getters["users/role"] === this.ROLE_ADMIN.value;
        },
        isCourier() {
            return this.$store.getters["users/role"] === this.ROLE_COURIER.value;
        },
        isOwner() {
            return this.$store.getters["users/role"] === this.ROLE_OWNER.value;
        },
        showSidebar() {
            return !this.isAdmin && !this.isCourier && this.accountPanelOpen;
        },
        accountPanelStyle() {
            return {
                "--theme-account-panel-background": this.$style["account-panel-background"] || "#083e60",
                "--theme-account-panel-border-color": this.$style["account-panel-border-color"] || "rgba(8, 62, 96, 0.18)",
            };
        },
        sidebarEntries() {
            return [
                {
                    to: { name: "home" },
                    titleI18n: "home",
                    rights: ["any"],
                },
                {
                    to: { name: "account" },
                    titleI18n: "account",
                    rights: ["any"],
                },
                {
                    to: { name: "restaurants" },
                    titleI18n: "restaurants",
                    rights: ["default.restaurants.read"],
                },
                {
                    to: { name: "subscription" },
                    titleI18n: "subscription",
                    rights: ["default.subscription.read"],
                    display: this.isNoShow,
                },
                {
                    to: { name: "rgpd" },
                    titleI18n: "rgpd",
                    rights: ["any"],
                    display: this.isOwner,
                },
                {
                    to: { name: "employees" },
                    titleI18n: "users",
                    rights: ["default.employees.read"],
                    display: this.isNoShow,
                },
                {
                    to: { name: "sms" },
                    titleI18n: "sms",
                    rights: ["default.sms.update"],
                },
                {
                    url: "#",
                    class: "open-intercom",
                    titleI18n: "help",
                    rights: ["any"],
                    display: this.isNoShow,
                },
                {
                    url: "#",
                    onClick: () => {
                        if (typeof Beacon !== "undefined") {
                            Beacon("open");
                        }
                    },
                    titleI18n: "help",
                    rights: ["any"],
                    display: this.isYservices,
                },
                {
                    url: "http://help.noshow.io/",
                    target: "_blank",
                    titleI18n: "documentation",
                    rights: ["any"],
                    display: this.isNoShow,
                },
                {
                    url: "https://aide.y-services.fr/",
                    target: "_blank",
                    titleI18n: "documentation",
                    rights: ["any"],
                    display: this.isYservices,
                },
                {
                    to: { name: "online_help" },
                    titleI18n: "onlineHelp",
                    rights: ["any"],
                    display: this.isNoShow,
                },
                {
                    to: { name: "app_updates.list" },
                    titleI18n: "appUpdates",
                    rights: ["any"],
                },
                {
                    to: { name: "restaurants.reserve_with_google" },
                    titleI18n: "rwg",
                    rights: ["any"],
                    display: this.isReserveWithGoogleEnabled() && this.isOwner,
                },
            ];
        },
    },
    data: function () {
        return {
            csrf: document.querySelector('meta[name="csrf-token"]').getAttribute("content"),
            accountPanelOpen: false,
        };
    },
    mounted() {
        document.addEventListener("keydown", this.handleKeydown);
    },
    beforeDestroy() {
        document.removeEventListener("keydown", this.handleKeydown);
    },
    watch: {
        $route() {
            this.closeAccountPanel();
        },
    },
    methods: {
        toggleAccountPanel() {
            this.accountPanelOpen = !this.accountPanelOpen;
        },
        closeAccountPanel() {
            if (!this.accountPanelOpen) {
                return;
            }

            this.accountPanelOpen = false;

            this.$nextTick(() => {
                const accountButton = this.$refs.topbar && this.$refs.topbar.$el.querySelector(".default-account-button");
                if (accountButton) {
                    accountButton.focus();
                }
            });
        },
        handleKeydown(event) {
            if (event.key === "Escape") {
                this.closeAccountPanel();
            }
        },
        logout() {
            axios
                .post("/logout")
                .then((response) => {
                    window.location.replace(response.data.redirect_url || response.headers.location || "/login");
                })
                .catch(() => {});
        },
    },
    components: {
        Topbar,
        Sidebar,
    },
};
</script>

<style lang="scss" scoped>
.account-panel-slide-enter-active,
.account-panel-slide-leave-active {
    transition:
        opacity 0.28s ease,
        transform 0.28s cubic-bezier(0.22, 1, 0.36, 1);
}

.account-panel-slide-enter,
.account-panel-slide-leave-to {
    opacity: 0;
    transform: translateX(32px) scale(0.98);
}

.account-panel-drawer {
    position: fixed;
    top: 60px;
    right: 0;
    bottom: 0;
    z-index: 1040;
    width: min(360px, calc(100vw - 12px));
    height: calc(100vh - 60px) !important;
    max-width: calc(100vw - 12px);
    max-height: calc(100vh - 60px);
    padding: 0 !important;
    background: var(--theme-account-panel-background) !important;
    border-left: 1px solid var(--theme-account-panel-border-color);
}
@media (max-width: 575px) {
    .account-panel-drawer {
        top: 60px;
        right: 0;
        bottom: 0;
        width: calc(100vw - 12px);
        height: calc(100vh - 60px) !important;
        max-width: calc(100vw - 12px);
        max-height: calc(100vh - 60px);
    }
}
</style>
